"use client";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ERPLeadBot from "./ERPLeadBot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, Globe, Clock } from "lucide-react";
import emailjs from "@emailjs/browser";
import Footer from "./Footer";

const productConfig = {
  school: {
    orgLabel: "School Name",
    orgPlaceholder: "e.g. Delhi Public School",
    emailPlaceholder: "principal@yourschool.com",
    phonePlaceholder: "+91 98765 43210",
    roles: [
      { value: "principal", label: "Principal / Director" },
      { value: "admin", label: "Administrator" },
      { value: "it", label: "IT Manager" },
      { value: "teacher", label: "Teacher" },
      { value: "other", label: "Other" },
    ],
  },
  bus: {
    orgLabel: "Organization Name",
    orgPlaceholder: "e.g. ABC Transport Pvt. Ltd.",
    emailPlaceholder: "you@yourcompany.com",
    phonePlaceholder: "+91 98765 43210",
    roles: [
      { value: "fleet_owner", label: "Fleet Owner" },
      { value: "ops_manager", label: "Operations Manager" },
      { value: "admin", label: "Administrator" },
      { value: "other", label: "Other" },
    ],
  },
  inventory: {
    orgLabel: "Business Name",
    orgPlaceholder: "e.g. Sharma Electronics",
    emailPlaceholder: "you@yourbusiness.com",
    phonePlaceholder: "+91 98765 43210",
    roles: [
      { value: "owner", label: "Business Owner" },
      { value: "store_manager", label: "Store / Warehouse Manager" },
      { value: "purchase_manager", label: "Purchase Manager" },
      { value: "it", label: "IT Manager" },
      { value: "other", label: "Other" },
    ],
  },
  default: {
    orgLabel: "Organization / Business Name",
    orgPlaceholder: "e.g. Your School or Business",
    emailPlaceholder: "you@example.com",
    phonePlaceholder: "+91 98765 43210",
    roles: [
      { value: "principal", label: "Principal / Director" },
      { value: "fleet_owner", label: "Fleet Owner" },
      { value: "owner", label: "Business Owner" },
      { value: "manager", label: "Manager" },
      { value: "admin", label: "Administrator" },
      { value: "other", label: "Other" },
    ],
  },
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const productParam = searchParams.get("product") || "default";
  const config = productConfig[productParam] || productConfig.default;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    orgName: "",
    product: productParam === "default" ? "" : productParam,
    role: "",
    subject: "",
    message: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const activeProduct = formData.product || productParam;
  const activeConfig = productConfig[activeProduct] || productConfig.default;

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role || !formData.subject) {
      alert("Please select both Role and Subject.");
      return;
    }

    setSubmitting(true);
    try {
      await emailjs.send(
        import.meta.env.VITE_REACT_APP_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          first_name: formData.name,
          last_name: "",
          email: formData.email,
          phone: formData.phone,
          school_name: formData.orgName,
          role: formData.role,
          subject: `[${formData.product || "General"}] ${formData.subject}`,
          message: formData.message,
          to_email: import.meta.env.VITE_REACT_APP_EMAILJS_TO_EMAIL,
        },
        import.meta.env.VITE_REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setFormSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        orgName: "",
        product: productParam === "default" ? "" : productParam,
        role: "",
        subject: "",
        message: "",
      });

      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-600">
              Have questions about Clynox? We are here to help. Reach out to our team and we will
              get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleFormSubmit} className="space-y-6">

                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="e.g. Rahul Sharma"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder={activeConfig.emailPlaceholder}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder={activeConfig.phonePlaceholder}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="orgName">{activeConfig.orgLabel} *</Label>
                      <Input
                        id="orgName"
                        name="orgName"
                        value={formData.orgName}
                        onChange={handleFormChange}
                        placeholder={activeConfig.orgPlaceholder}
                        required
                      />
                    </div>

                    {/* Show product selector only when not arriving from a specific product page */}
                    {productParam === "default" && (
                      <div className="space-y-2">
                        <Label htmlFor="product">Interested In *</Label>
                        <Select
                          value={formData.product}
                          onValueChange={(value) =>
                            setFormData((prev) => ({ ...prev, product: value, role: "" }))
                          }
                        >
                          <SelectTrigger id="product">
                            <SelectValue placeholder="Select a product" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="school">School Management System</SelectItem>
                            <SelectItem value="bus">Bus Management System</SelectItem>
                            <SelectItem value="inventory">Inventory Management System</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="role">Your Role *</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                      >
                        <SelectTrigger id="role">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeConfig.roles.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger id="subject">
                          <SelectValue placeholder="What can we help you with?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="demo">Request a Demo</SelectItem>
                          <SelectItem value="pricing">Pricing Information</SelectItem>
                          <SelectItem value="support">Technical Support</SelectItem>
                          <SelectItem value="features">Feature Inquiry</SelectItem>
                          <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleFormChange}
                        placeholder="Tell us more about your needs..."
                        rows={5}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={submitting}
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>

                    {formSubmitted && (
                      <p className="text-green-600 text-center font-medium">
                        Thank you! We will get back to you soon.
                      </p>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Email</div>
                      <a href="mailto:clynoxofficial@gmail.com" className="text-gray-600 hover:text-blue-600">
                        clynoxofficial@gmail.com
                      </a>
                      <div className="text-sm text-gray-500 mt-1">We will respond within 24 hours</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Phone</div>
                      <a href="tel:+917499070944" className="text-gray-600 hover:text-blue-600">
                        +91-7499070944
                      </a>
                      <div className="text-sm text-gray-500 mt-1">Mon-Fri, 9am-6pm IST</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Website</div>
                      <a href="https://www.clynox.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600">
                        www.clynox.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Business Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Monday - Friday</span>
                        <span className="text-gray-900 font-semibold">9:00 AM - 6:00 PM</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Saturday</span>
                        <span className="text-gray-900 font-semibold">10:00 AM - 4:00 PM</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Sunday</span>
                        <span className="text-gray-900 font-semibold">Closed</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
                <CardHeader>
                  <CardTitle className="text-white">Need Immediate Help?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-100 mb-4">
                    Check out our comprehensive help center for instant answers to common questions.
                  </p>
                  <Button
                    variant="secondary"
                    className="w-full bg-white text-blue-600 hover:bg-gray-100"
                    onClick={() => window.dispatchEvent(new Event("clynox-open-erp-bot"))}
                  >
                    Visit Help Center
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Find quick answers to common questions about Clynox</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How long does implementation take?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Most schools are up and running within 2-4 weeks. We provide dedicated support and
                  training throughout the implementation process.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do you offer training for staff?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes! We provide comprehensive training for all staff members, including video
                  tutorials, live webinars, and on-site training options.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is my data secure?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Absolutely. We use 256-bit encryption, daily backups, and comply with all major
                  data protection regulations including GDPR.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I migrate existing data?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes, we help you migrate all your existing data from spreadsheets, other systems,
                  or paper records at no extra cost.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
